import type { ComponentProps } from 'react';
import { cn } from '../lib/cn.js';

// Control borders use `border-input` (the brand's border-control token): 3:1 against every
// surface (WCAG 1.4.11), checked by brand:check.
export const fieldBase = [
  'w-full min-w-0 rounded-md border border-input bg-surface-raised text-body text-fg shadow-xs',
  'placeholder:text-muted',
  'transition-[border-color,box-shadow] duration-(--duration-fast) ease-standard',
  // Focus looks the same on a field as on a button: the 2 px ring, offset (B-018).
  'outline-none focus-visible:border-focus-ring focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
  // Disabled reads as disabled in both themes: sunken, no shadow, a quiet border, muted text.
  'disabled:cursor-not-allowed disabled:border-border disabled:bg-surface-sunken disabled:text-muted disabled:opacity-60 disabled:shadow-none',
  'aria-invalid:border-state-danger aria-invalid:ring-state-danger/25 aria-invalid:focus-visible:ring-2',
].join(' ');

/**
 * Read-only is not disabled: the value is real and selectable, it just can't change here.
 * Text inputs only (`:read-only` also matches buttons, so this can't live in fieldBase).
 */
const readOnlyField =
  'read-only:not-disabled:border-dashed read-only:not-disabled:bg-surface read-only:not-disabled:shadow-none';

export function Input({ className, type = 'text', ...props }: ComponentProps<'input'>) {
  return (
    <input
      type={type}
      className={cn(
        fieldBase,
        'h-control px-control-x',
        readOnlyField,
        'file:mr-3 file:border-0 file:bg-transparent file:font-medium file:text-fg',
        type === 'number' && 'tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return (
    <textarea
      className={cn(
        fieldBase,
        'min-h-24 px-control-x py-2 [field-sizing:content]',
        readOnlyField,
        className,
      )}
      {...props}
    />
  );
}
